import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../api";
import { ApiError } from "../api";
import { Chip, Ic } from "../components/ui";
import { useSession } from "../session";
import { t as tr, useT } from "../i18n";
import type { Cheie } from "../i18n";

/** Cine are acces la proiect — și cum se dă mai departe.
 *
 *  Sus, un singur rând: **toată organizația**. El rezolvă întrebarea „dar cine
 *  intră în echipă luna viitoare?" — nu se copiază în rânduri per om, deci se
 *  aplică din prima clipă și celor care nu existau când a fost dat.
 *
 *  Dedesubt, **oamenii**, iar lista aceea e COMPUSĂ din trei surse, fiindcă
 *  altfel ar minți prin omisiune — cine se uită la ea vrea să afle cine ajunge
 *  la proiect, nu ce rânduri există în tabelă:
 *
 *    * `oficiu`      — owner-ii și adminii organizației, care au `admin` pe tot
 *                      ce e al ei, fără grant. Se arată, nu se ating.
 *    * `personal`    — granturile date omului. Doar ele se pot șterge.
 *    * `organizatie` — membrii care iau rolul din grantul de sus. Nu au rând în
 *                      tabelă și nu trebuie să aibă: dacă ar fi copiați acolo,
 *                      cine intră mâine în echipă n-ar mai primi nimic.
 *
 *  Rolul cel mai TARE câștigă: dacă tot biroul are „Editare" și tu ai
 *  „Vizualizare", editezi. De-aia rândul de organizație e o PODEA, nu un
 *  plafon — altfel „dă-le acces tuturor" ar putea LUA drepturi cuiva. Și de-aia
 *  treptele de sub el sunt stinse în select: alese, n-ar schimba nimic. */

const ROLURI: { key: api.ProjectRole; eticheta: Cheie }[] = [
  { key: "viewer", eticheta: "acces.rol_viewer" },
  { key: "editor", eticheta: "acces.rol_editor" },
  { key: "admin", eticheta: "acces.rol_admin" },
];

const RANG: Record<api.ProjectRole, number> = { viewer: 1, editor: 2, admin: 3 };

/** Ce poți TU, spus la persoana a doua. */
const ESTI: Record<api.ProjectRole, Cheie> = {
  viewer: "acces.esti_viewer",
  editor: "acces.esti_editor",
  admin: "acces.esti_admin",
};

const ERORI: Record<string, Cheie> = {
  s_ar_incuia_afara: "acces.incuiat_afara",
  not_a_member: "acces.nu_e_membru",
  forbidden: "acces.interzis",
  invalid_role: "acces.rol_inexistent",
};

type Sursa = "oficiu" | "personal" | "organizatie";

type Rand = {
  user: api.AccessUser;
  rol: api.ProjectRole;
  sursa: Sursa;
  /** doar pe granturile personale — doar ele se pot șterge */
  grantId?: string;
  /** doar pe cele din oficiu */
  orgRole?: api.OrgRole;
};

export default function Acces({ projectId }: { projectId: string }) {
  const { user } = useSession();
  const t = useT();
  const [acces, setAcces] = useState<api.ProjectAccess | null>(null);
  const [membri, setMembri] = useState<api.Member[]>([]);
  const [deAdaugat, setDeAdaugat] = useState("");
  const [rolNou, setRolNou] = useState<api.ProjectRole>("editor");
  const [eroare, setEroare] = useState("");
  const [ocupat, setOcupat] = useState(false);

  useEffect(() => {
    let viu = true;
    api.getAccess(projectId)
      .then((a) => viu && setAcces(a))
      .catch(() => viu && setEroare(t("acces.esec_citire")));
    return () => { viu = false; };
  }, [projectId]);

  // Lista din care se alege cine primește acces e lista organizației
  // PROIECTULUI, nu a userului: cele două diferă de îndată ce cineva e în două
  // organizații. De-aia se cere abia după ce știm despre ce organizație e vorba.
  const orgId = acces?.organization.id;
  useEffect(() => {
    if (!orgId) return;
    let viu = true;
    api.listMembers(orgId)
      .then((m) => viu && setMembri(m))
      .catch(() => undefined);
    return () => { viu = false; };
  }, [orgId]);

  const cere = useCallback(async (lucru: () => Promise<api.ProjectAccess>) => {
    setOcupat(true);
    setEroare("");
    try {
      setAcces(await lucru());
    } catch (err) {
      setEroare(err instanceof ApiError
        ? (ERORI[err.code] ? tr(ERORI[err.code])
                           : err.message || tr("acces.esec_schimbare"))
        : tr("acces.esec_schimbare"));
    } finally {
      setOcupat(false);
    }
  }, []);

  // Rândurile de oameni, compuse din cele trei surse (v. antetul).
  const randuri = useMemo<Rand[]>(() => {
    if (!acces) return [];
    const oficiu: Rand[] = acces.implicit.map((i) => ({
      user: i.user, rol: "admin", sursa: "oficiu", orgRole: i.org_role,
    }));
    const personale: Rand[] = acces.users.map((g) => ({
      user: g.user, sursa: "personal", grantId: g.id,
      // Rolul AFIȘAT e cel efectiv: grantul pe organizație e o podea, deci un
      // grant personal mai mic nu coboară pe nimeni sub el.
      rol: acces.org_grant && RANG[acces.org_grant.role] > RANG[g.role]
        ? acces.org_grant.role : g.role,
    }));
    const acoperiti = new Set([...oficiu, ...personale].map((r) => r.user.id));
    const dinOrg: Rand[] = acces.org_grant
      ? membri
        .filter((m) => !acoperiti.has(m.user_id))
        .map((m) => ({
          user: { id: m.user_id, email: m.email, full_name: m.full_name,
                  disabled: m.disabled },
          rol: acces.org_grant!.role,
          sursa: "organizatie" as Sursa,
        }))
      : [];
    return [...oficiu, ...personale, ...dinOrg];
  }, [acces, membri]);

  if (acces === null) {
    return (
      <div className="setari-pagina">
        <p className="note">{eroare || t("comun.se_incarca")}</p>
      </div>
    );
  }

  const poate = acces.can_manage && !ocupat;
  const podea = acces.org_grant ? RANG[acces.org_grant.role] : 0;
  const acoperiti = new Set(randuri.map((r) => r.user.id));
  const candidati = membri.filter((m) => !acoperiti.has(m.user_id));

  function schimbaOrg(valoare: string) {
    if (valoare === "") {
      if (!acces?.org_grant) return;
      if (!confirm(t("acces.confirma_scoatere_org",
                     { organizatie: acces.organization.name }))) return;
      const id = acces.org_grant.id;
      cere(() => api.revokeAccess(projectId, id));
      return;
    }
    cere(() => api.grantAccess(projectId, {
      subject: "organization", role: valoare as api.ProjectRole,
    }));
  }

  function adauga() {
    if (!deAdaugat) return;
    cere(() => api.grantAccess(projectId, {
      subject: "user", user_id: deAdaugat, role: rolNou,
    }));
    setDeAdaugat("");
  }

  function scoate(rand: Rand) {
    const nume = rand.user.full_name || rand.user.email;
    if (!rand.grantId) return;
    if (!confirm(t("acces.confirma_scoatere", { nume }))) return;
    cere(() => api.revokeAccess(projectId, rand.grantId!));
  }

  return (
    <div className="setari-pagina">
      <div className="setari-titlu">
        <h2 className="disp">{t("setari.acces")}</h2>
        {acces.role && <Chip tone="mut">{t(ESTI[acces.role])}</Chip>}
      </div>

      {eroare && <p className="note bad setari-eroare">{eroare}</p>}
      {!acces.can_manage && (
        <p className="note setari-eroare">{t("acces.doar_vezi")}</p>
      )}

      <h3 className="setari-sectiune">{t("acces.organizatia")}</h3>
      <div className="acces-rand">
        <span className="av av-org"><Ic n="users" s={15} /></span>
        <div className="acces-cine">
          <div className="name">
            {t("acces.toti_din", { organizatie: acces.organization.name })}
          </div>
          <div className="meta">
            {t(acces.org_grant ? "acces.inclusiv_viitorii" : "acces.nu_e_pe_org")}
          </div>
        </div>
        <div className="sp" />
        <SelectRol valoare={acces.org_grant?.role ?? ""} cuFaraAcces
                   dezactivat={!poate} onChange={schimbaOrg} />
      </div>

      <h3 className="setari-sectiune">{t("acces.oameni")}</h3>
      <ul className="rows acces-lista">
        {randuri.map((rand) => (
          <li key={rand.user.id}
              className={`row ${rand.user.disabled ? "dim" : ""}`}>
            <span className="av">{initiale(rand.user)}</span>
            <div className="acces-cine">
              <div className="name">
                {rand.user.full_name || rand.user.email}
                {rand.user.id === user?.id && (
                  <span className="acces-tu">{t("acces.tu")}</span>
                )}
              </div>
              <div className="meta">{rand.user.email}</div>
            </div>
            <div className="sp" />
            {rand.user.disabled && <Chip tone="bad">{t("acces.dezactivat")}</Chip>}
            {rand.sursa === "oficiu" && (
              <Chip tone="acc">
                {t("acces.al_organizatiei", { rol: rand.orgRole ?? "" })}
              </Chip>
            )}
            {rand.sursa === "organizatie" && (
              <Chip tone="mut">{t("acces.din_organizatie")}</Chip>
            )}
            {rand.sursa === "oficiu" ? (
              /* Rolul lor nu e un rând care se schimbă de aici, ci consecința
                 rolului din organizație — deci se scrie, nu se alege. */
              <span className="acces-fix">{t("acces.rol_admin")}</span>
            ) : (
              <SelectRol valoare={rand.rol} dezactivat={!poate} podea={podea}
                         onChange={(rol) => cere(() => api.grantAccess(projectId, {
                           subject: "user", user_id: rand.user.id,
                           role: rol as api.ProjectRole,
                         }))} />
            )}
            {/* „×" doar pe granturile personale: pe un rând venit din
                organizație n-ar avea ce șterge, iar accesul i-l ia rândul de
                sus, nu ăsta. */}
            {acces.can_manage && rand.grantId ? (
              <button className="btn btng" disabled={ocupat}
                      title={t("acces.scoate")} onClick={() => scoate(rand)}>
                <Ic n="x" s={13} />
              </button>
            ) : acces.can_manage && <span className="acces-fara-x" />}
          </li>
        ))}
        {randuri.length === 0 && (
          <li className="note">{t("acces.nimeni")}</li>
        )}
      </ul>

      {acces.can_manage && (
        <form className="row-form acces-adauga"
              onSubmit={(e) => { e.preventDefault(); adauga(); }}>
          <select className="inp" value={deAdaugat}
                  disabled={ocupat || candidati.length === 0}
                  onChange={(e) => setDeAdaugat(e.target.value)}>
            <option value="">
              {t(candidati.length === 0 ? "acces.toti_au_acces"
                                        : "acces.alege_om")}
            </option>
            {candidati.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name ? `${m.full_name} (${m.email})` : m.email}
              </option>
            ))}
          </select>
          <select className="inp acces-rol" value={rolNou} disabled={ocupat}
                  onChange={(e) => setRolNou(e.target.value as api.ProjectRole)}>
            {ROLURI.map((r) => (
              <option key={r.key} value={r.key}>{t(r.eticheta)}</option>
            ))}
          </select>
          <button className="btn btnp" type="submit" disabled={!deAdaugat || ocupat}>
            <Ic n="plus" s={14} />{t("comun.adauga")}
          </button>
        </form>
      )}
    </div>
  );
}

function SelectRol({ valoare, cuFaraAcces = false, dezactivat, podea = 0, onChange }: {
  valoare: string;
  cuFaraAcces?: boolean;
  dezactivat: boolean;
  /** rangul grantului pe organizație: sub el, o alegere n-ar schimba nimic */
  podea?: number;
  onChange: (valoare: string) => void;
}) {
  const t = useT();
  return (
    <select className="inp acces-rol" value={valoare} disabled={dezactivat}
            onChange={(e) => onChange(e.target.value)}>
      {cuFaraAcces && <option value="">{t("acces.fara_acces")}</option>}
      {ROLURI.map((r) => (
        <option key={r.key} value={r.key} disabled={RANG[r.key] < podea}>
          {t(r.eticheta)}
          {RANG[r.key] < podea ? t("acces.sub_organizatie") : ""}
        </option>
      ))}
    </select>
  );
}

function initiale(om: api.AccessUser): string {
  return (om.full_name || om.email)
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? "")
    .join("");
}
